import { useState } from 'react';
import { LoadingAnimatedInline } from '../components/Loading';
import {
  AdminConfirmationPopup
} from '../components/AdminConfirmationPopup';

import { useAuth } from '../auth/AuthContext';

const DownloadNew: React.FC = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  // @ts-ignore
  const [showSuccess, setShowSuccess] = useState(false);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [projectName, setProjectName] = useState<string>('');
  const [accessType, setAccessType] = useState<string>('All');
  const [icon, setIcon] = useState<string>('softwareItems/terrastation-core.jpg');
  const [description, setDescription] = useState<string>('');

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let size = bytes / 1024;
    let i = 0;
    while (size >= 1024 && i < units.length - 1) {
      size /= 1024;
      i++;
    }
    return `${size.toFixed(2)} ${units[i]}`;
  };

  const handleUpload = async (file: File) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('projectName', projectName);
      formData.append('storagePath', `downloads/${file.name}`);
      formData.append('file', file);
      formData.append('access', accessType);
      formData.append('icon', icon);
      formData.append('description', description);
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/uploadDownload', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!res.ok) {
        throw new Error(`Upload failed: ${res.statusText}`);
      }

      setLoading(false);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (e) {
      setLoading(false);
      throw new Error(
        `Failed to upload file: ${e instanceof Error ? e.message : String(e)}`
      );
    }
  };

  const handleUploadConfirm = (file: File) => {
    if (pendingFile) {
      handleUpload(file).catch((e) => {
        alert(e instanceof Error ? e.message : String(e));
      });
    }
    setShowConfirmation(false);
    setPendingFile(null);
  };

  const handleCancelUpload = () => {
    setShowConfirmation(false);
    setPendingFile(null);
  };

  const handleUploadPrompt = (file: File | null) => {
    if (!file) {
      alert('Please select a file to upload.');
      return;
    }
    setPendingFile(file);
    setShowConfirmation(true);
  };
 

  if (loading) return <LoadingAnimatedInline />;
  return (
    <div className="p-6 max-w-5/6 mx-auto h-full">
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-1">
          Enter a name for the new project download. Note that this form is not
          uploading updates for existing projects.
        </p>
        <input
          type="text"
          id="project-name-input"
          value={projectName}
          onChange={(e) => setProjectName(e.target.value)}
          className="w-full px-3 py-2 border border-light-border rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
          placeholder="Example: TerraStation Core"
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Upload File
        </label>
        <input
          id="file-0"
          type="file"
          className="hidden"
          onChange={(e) => {
            const file = (e.target as HTMLInputElement).files?.[0] || null;
            setPendingFile(file);
            const nameEl = document.getElementById('downloadnew-selectedfile-name');
            const sizeEl = document.getElementById('downloadnew-selectedfile-size');
            if (file) {
              if (nameEl) nameEl.textContent = file.name;
              if (sizeEl) sizeEl.textContent = formatSize(file.size);
            } else {
              if (nameEl) nameEl.textContent = 'example.zip';
              if (sizeEl) sizeEl.textContent = '123.45 MB';
            }
          }}
        />
        <button
          id="upload-button-0"
          className="px-4 py-2 bg-light-main text-white rounded-md cursor-pointer hover:bg-light-main-hover transition-colors duration-200 ease-in-out font-medium"
          onClick={() => document.getElementById(`file-0`)?.click()}
        >
          Select File
        </button>

        <div className="mt-2 text-sm text-gray-700">
          <p id="downloadnew-selectedfile-name">example.zip</p>
          <p id="downloadnew-selectedfile-size">123.45 MB</p>
        </div>
      </div>
      <p className="text-sm text-gray-600 mb-1">
        Enter a storage path for the file in Firebase Storage. Default
      </p>
      <div className="mb-4 flex flex-col md:flex-row gap-2">
        <input
          type="text"
          className="w-fit px-3 py-2 border border-light-border rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
          placeholder="path/in/firebase/"
          defaultValue="downloads/"
        />
        <input
          type="text"
          className="w-full px-3 py-2 border border-light-border rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
          placeholder="example.zip"
        />
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Access
        </label>
        <select
          value={accessType}
          onChange={(e) => setAccessType(e.target.value)}
          className="w-full px-3 py-2 border border-light-border rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
        >
          <option value="All">All</option>
        </select>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Icon
        </label>
        <select
          value={icon}
          onChange={(e) => setIcon(e.target.value)}
          className="w-full px-3 py-2 border border-light-border rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
        >
          <option value="softwareItems/terrastation-core.jpg">softwareItems/terrastation-core.jpg</option>
        </select>
      </div>
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border border-light-border rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
          placeholder="Enter a description for this download"
          rows={4}
        />
      </div>

      <div className="text-right">
        <button
          className="px-4 py-2 bg-light-main cursor-pointer text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out font-medium"
          onClick={() => {
              const fileInput = document.getElementById(`file-0`) as HTMLInputElement;
              const file = fileInput?.files?.[0] || null;
              handleUploadPrompt(file);

          }}
        >
          Submit
        </button>
      </div>
      {showConfirmation && (
        <AdminConfirmationPopup
          file={pendingFile}
          projectName={projectName}
          onProjectNameChange={setProjectName}
          onConfirm={() => handleUploadConfirm(pendingFile!)}
          onCancel={handleCancelUpload}
        />
      )}
    </div>
  );
};

export default DownloadNew;
